import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined'
import CloseIcon from '@mui/icons-material/Close'
import {useDispatch, useSelector} from 'react-redux'
import {parse} from 'json2csv'
import Papa from 'papaparse'
import {DataGrid} from '@mui/x-data-grid'
import {actions as importCsvActions} from '../../redux/modules/importCsv'
import {RootState} from '../../redux/rootReducer'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Typography,
} from '@mui/material'
import {ModalPage} from 'nav/ModalPage'
import FileDropZone from '../../components/FileDropZone'
import FileDetails from '../../components/FileDetails'
import TableTooltip from '../../components/TableTooltip'
import styles from './styles'
import AlertMessage from '../../components/AlertMessage'
import startDownload from '../../startDownload'
import addedPatientColumns from './addedPatientColumns'
import addedDeviceColumns from './addedDeviceColumns'
import previewColumns from './previewColumns'
import validationRowColumns from './validationRowColumns'
import ValidationSummaryTiles from './ValidationSummaryTiles'
import buildErrorFileRows from './buildErrorFileRows'
import {getMissingRequiredColumns, readCsvHeaderColumns} from './csvHeaderValidation'

const CSV_FORMAT_COLUMNS = [
  'Patient Name',
  'Prescriber',
  'Patient DOB',
  'Device Name',
  'Serial Number',
]

const CSV_FORMAT_ROWS = [
  ['Day, Jennifer A', 'Vraney, Robert T.', '1972-08-15', '74011', 'AG25020271'],
  ['Perez, Debra', 'Shi, Chen', '1975-03-17', '73947', 'AG25020289'],
  ['Smith, Carl', 'Marsh, Robert Anthony', '1970-03-21', '73434', 'AG25020272'],
]

const SAMPLE_CSV_PATH = `${process.env.PUBLIC_URL ?? ''}/assets/import_csv_sample.csv`

const PREVIEW_ROW_LIMIT = 10

// Compact-density header + row heights (MUI DataGrid). Used to size each summary table to fit
// its actual row count exactly, capped at SUMMARY_TABLE_MAX_HEIGHT — `autoHeight` was tried here
// first, but it renders every row with no internal scroll, so a large import (20+ rows) grows
// the table past the modal's own scroll boundary instead of scrolling inside its own box. A grid
// with an explicit height virtualizes and scrolls internally instead, regardless of row count.
const SUMMARY_TABLE_HEADER_HEIGHT = 40
const SUMMARY_TABLE_ROW_HEIGHT = 36
const SUMMARY_TABLE_MAX_HEIGHT = 300

const summaryTableHeight = (rowCount: number): number =>
  Math.min(
    SUMMARY_TABLE_MAX_HEIGHT,
    SUMMARY_TABLE_HEADER_HEIGHT + rowCount * SUMMARY_TABLE_ROW_HEIGHT,
  )

const ImportCsvPage: ModalPage = ({onClickCancel}) => {
  const [selectedFile, setSelectedFile] = useState<File | undefined>()
  const [loadingStep, setLoadingStep] = useState<string | undefined>()
  const [step, setStep] = useState<'select' | 'preview' | 'validate' | 'review'>('select')
  const [previewRows, setPreviewRows] = useState<(Record<string, string> & {id: string})[]>([])
  const [previewTotalRowCount, setPreviewTotalRowCount] = useState(0)
  // Captured at upload time and kept independent of `selectedFile`, which the effect below
  // clears as soon as `uploading` flips back to false — right when the summary/failure UI
  // (which needs the file name) first becomes visible.
  const [uploadedFileName, setUploadedFileName] = useState<string | undefined>()
  // "Confirm & Import" reuses handleUpload, whose resetFeedback() clears validationResult
  // synchronously — before `uploading` flips true (it only does once the async FileReader read
  // resolves). This flag covers that gap so the validate/review box shows a spinner instead of
  // going blank.
  const [approving, setApproving] = useState(false)
  const dispatch = useDispatch()
  const {
    uploading,
    error: errorMessage,
    successSummary,
    failureSummary,
    uploadFailed,
    validating,
    validationResult,
    validationError,
  } = useSelector(({importCsv}: RootState) => importCsv)
  const previousUploadingRef = useRef(uploading)
  // Guards against a stale header-validation result clobbering state if the user
  // picks a different file while the previous file's async header check is in flight.
  const latestFileRef = useRef<File | undefined>()
  // Tracks the most recently selected file so a slow Papa.parse call for a file the user has
  // since replaced can't overwrite the preview with stale data once it finally resolves.
  const selectedFileRef = useRef<File | undefined>()

  const resetFeedback = useCallback(() => {
    dispatch(importCsvActions.reset())
  }, [dispatch])

  const isCsvFile = useCallback((file: File) => {
    const fileName = file.name?.toLowerCase() ?? ''
    return (
      fileName.endsWith('.csv') ||
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel'
    )
  }, [])

  // Shared by every client-side rejection path (wrong type, unreadable file, missing
  // columns) so they can't drift from each other as new checks are added over time.
  const rejectSelectedFile = useCallback(
    (message: string) => {
      selectedFileRef.current = undefined
      setSelectedFile(undefined)
      resetFeedback()
      dispatch(importCsvActions.setError({error: {message, details: []}}))
      setLoadingStep(undefined)
    },
    [dispatch, resetFeedback],
  )

  const applySelectedFile = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) {
        return
      }
      const file = fileList[0]
      latestFileRef.current = file
      if (!isCsvFile(file)) {
        rejectSelectedFile('Please select a CSV file (*.csv).')
        return
      }

      let headerColumns: string[]
      try {
        headerColumns = await readCsvHeaderColumns(file)
      } catch {
        if (latestFileRef.current !== file) {
          return
        }
        rejectSelectedFile('Unable to read the CSV file. Please try again.')
        return
      }
      if (latestFileRef.current !== file) {
        return
      }

      const missingColumns = getMissingRequiredColumns(headerColumns, CSV_FORMAT_COLUMNS)
      if (missingColumns.length > 0) {
        rejectSelectedFile(`CSV is missing required column(s): ${missingColumns.join(', ')}.`)
        return
      }

      resetFeedback()
      selectedFileRef.current = file
      setSelectedFile(file)
      setLoadingStep(undefined)

      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: results => {
          if (selectedFileRef.current !== file) {
            return
          }
          setPreviewRows(
            results.data
              .slice(0, PREVIEW_ROW_LIMIT)
              .map((row, index) => ({...row, id: String(index)})),
          )
          setPreviewTotalRowCount(results.data.length)
          setStep('preview')
        },
        error: () => {
          if (selectedFileRef.current !== file) {
            return
          }
          selectedFileRef.current = undefined
          setSelectedFile(undefined)
          dispatch(
            importCsvActions.setError({
              error: {message: 'Unable to parse the CSV file. Please try again.', details: []},
            }),
          )
        },
      })
    },
    [isCsvFile, rejectSelectedFile, resetFeedback],
  )

  const handleFilesSelected = useCallback(
    (files: FileList) => {
      if (uploading) {
        return
      }
      applySelectedFile(files)
    },
    [applySelectedFile, uploading],
  )

  const handleClearSelection = useCallback(() => {
    // Invalidate so a header check still in flight for a since-cleared file can't
    // resurrect it or dispatch a stray error once it resolves.
    latestFileRef.current = undefined
    selectedFileRef.current = undefined
    setSelectedFile(undefined)
    setUploadedFileName(undefined)
    resetFeedback()
    setLoadingStep(undefined)
    setStep('select')
    setPreviewRows([])
    setPreviewTotalRowCount(0)
  }, [resetFeedback])

  const handleBackToSelect = useCallback(() => {
    setStep('select')
  }, [])

  const handleBackToValidate = useCallback(() => {
    setStep('validate')
  }, [])

  // Mirrors the button's own disabled gate so the review step stays unreachable for a result
  // with errors even if the button state were ever bypassed.
  const handleReview = useCallback(() => {
    if (!validationResult || validationResult.errorCount > 0) {
      return
    }
    setStep('review')
  }, [validationResult])

  const handleValidate = useCallback(() => {
    if (!selectedFile || uploading || validating) {
      return
    }
    dispatch(importCsvActions.validate({file: selectedFile}))
    setStep('validate')
  }, [dispatch, selectedFile, uploading, validating])

  const handleCancel = useCallback(() => {
    handleClearSelection()
    onClickCancel()
  }, [handleClearSelection, onClickCancel])

  const formatFileSize = useCallback((bytes: number) => {
    if (bytes === 0) {
      return '0 B'
    }
    const units = ['B', 'KB', 'MB', 'GB']
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
    const value = bytes / Math.pow(1024, unitIndex)
    const precision = value >= 10 || unitIndex === 0 ? 0 : 1
    return `${value.toFixed(precision)} ${units[unitIndex]}`
  }, [])

  const formatProcessingTime = useCallback((processingTimeMs: number) => {
    if (processingTimeMs < 1000) {
      return `${processingTimeMs} ms`
    }
    return `${(processingTimeMs / 1000).toFixed(1)} s`
  }, [])

  const handleDownloadErrorFile = useCallback(() => {
    if (!failureSummary?.failedRows.length) {
      return
    }
    try {
      const csv = parse(buildErrorFileRows(failureSummary.failedRows))
      startDownload(csv, `csv-import-errors-${uploadedFileName ?? 'upload'}-${Date.now()}.csv`)
    } catch {
      dispatch(
        importCsvActions.setError({
          error: {message: 'Failed to generate the error file. Please try again.', details: []},
        }),
      )
    }
  }, [dispatch, failureSummary, uploadedFileName])

  const handleUpload = useCallback(async () => {
    if (!selectedFile || uploading) {
      return
    }
    const file = selectedFile
    resetFeedback()
    setLoadingStep('Uploading file...')
    try {
      await new Promise<void>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve()
        reader.onerror = () => reject(new Error('Unable to read the CSV file. Please try again.'))
        reader.readAsText(file)
      })
      setLoadingStep('Processing file...')
      setUploadedFileName(file.name)
      dispatch(importCsvActions.upload({file}))
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : 'Upload failed.'
      dispatch(importCsvActions.setError({error: {message: message, details: []}}))
      setLoadingStep(undefined)
      setSelectedFile(undefined)
    }
  }, [dispatch, resetFeedback, selectedFile, uploading])

  const handleConfirmImport = useCallback(async () => {
    if (!validationResult || validationResult.errorCount > 0) {
      return
    }
    setApproving(true)
    await handleUpload()
    setApproving(false)
  }, [handleUpload, validationResult])

  useEffect(() => {
    if (previousUploadingRef.current && !uploading && loadingStep) {
      setLoadingStep(undefined)
      setSelectedFile(undefined)
      setStep('select')
      setPreviewRows([])
      setPreviewTotalRowCount(0)
    }
    previousUploadingRef.current = uploading
  }, [loadingStep, uploading])

  useEffect(() => {
    return () => {
      resetFeedback()
      setSelectedFile(undefined)
      setUploadedFileName(undefined)
      setLoadingStep(undefined)
      setStep('select')
      setPreviewRows([])
      setPreviewTotalRowCount(0)
    }
  }, [resetFeedback])

  const validationIssueRows = useMemo(
    () =>
      (validationResult?.rows ?? [])
        .filter(row => row.status !== 'valid')
        .map(row => ({...row, id: row.rowNumber})),
    [validationResult],
  )

  // Shown on both the validate and review steps so the "nothing is skipped, warnings included"
  // message can't drift between them.
  const warningNote = useMemo(() => {
    if (!validationResult || validationResult.errorCount > 0 || validationResult.warningCount === 0) {
      return null
    }
    return (
      <Typography variant="body2" color="text.secondary" sx={{marginTop: 1}}>
        Approving will upload all {validationResult.totalRows} row(s), including those with
        warnings.
      </Typography>
    )
  }, [validationResult])

  const fileDetails = useMemo(() => {
    if (!selectedFile) {
      return null
    }
    return (
      <FileDetails
        name={selectedFile.name}
        sizeLabel={formatFileSize(selectedFile.size)}
        helperText="The file will be validated once uploaded."
        onRemove={handleClearSelection}
        disabled={uploading}
      />
    )
  }, [formatFileSize, handleClearSelection, selectedFile, uploading])

  const titleElement = (
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
      <Typography variant="subtitle1">CSV Format Example</Typography>
      <Button
        variant="text"
        size="small"
        href={SAMPLE_CSV_PATH}
        download
        sx={{
          color: 'common.white',
          boxShadow: 'none',
          '&:hover': {bgcolor: 'grey.500', boxShadow: 'none'},
        }}>
        Download Sample CSV
      </Button>
    </Box>
  )

  return (
    <Container component="main">
      <Box sx={styles.modal}>
        <IconButton
          aria-label="Close import CSV modal"
          onClick={handleCancel}
          sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={styles.scrollableContent}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, marginBottom: 3}}>
            <Typography variant="h4" sx={{...styles.headerLabel, marginBottom: 0}}>
              Import CSV
            </Typography>
            <TableTooltip
              columns={CSV_FORMAT_COLUMNS}
              rows={CSV_FORMAT_ROWS}
              title={titleElement}
              icon={<HelpOutlinedIcon fontSize="medium" />}
              iconButtonProps={{'aria-label': 'CSV format example'}}
            />
          </Box>

          <Typography variant="body1" sx={{marginBottom: 3}}>
            Upload a CSV file with patients and devices data. You can drag and drop a file or
            browse your computer to select one manually.
          </Typography>

          {uploadFailed && (
            <Alert severity="warning" sx={styles.feedback}>
              No changes were made to the database.
            </Alert>
          )}

          {failureSummary && (
            <Box sx={styles.summary}>
              <Box sx={styles.summaryStats}>
                <Typography variant="body2">Batch ID: {failureSummary.batchId}</Typography>
                <Typography variant="body2">Total rows: {failureSummary.totalRows}</Typography>
                <Typography variant="body2">Success: {failureSummary.successCount}</Typography>
                <Typography variant="body2">Failed: {failureSummary.failedCount}</Typography>
                <Typography variant="body2">
                  Processing time: {formatProcessingTime(failureSummary.processingTimeMs)}
                </Typography>
              </Box>

              {failureSummary.addedPatients.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={styles.summaryTableLabel}>
                    Added patients ({failureSummary.addedPatients.length})
                  </Typography>
                  <Box sx={styles.summaryTableWrapper}>
                    <DataGrid
                      sx={{height: summaryTableHeight(failureSummary.addedPatients.length)}}
                      density="compact"
                      hideFooter
                      rows={failureSummary.addedPatients}
                      columns={addedPatientColumns}
                    />
                  </Box>
                </>
              )}

              {failureSummary.addedDevices.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={styles.summaryTableLabel}>
                    Added devices ({failureSummary.addedDevices.length})
                  </Typography>
                  <Box sx={styles.summaryTableWrapper}>
                    <DataGrid
                      sx={{height: summaryTableHeight(failureSummary.addedDevices.length)}}
                      density="compact"
                      hideFooter
                      rows={failureSummary.addedDevices}
                      columns={addedDeviceColumns}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}

          <AlertMessage error={errorMessage} severity="error" sx={styles.feedback} />

          {failureSummary && failureSummary.failedRows.length > 0 && (
            <Box sx={{marginBottom: 2}}>
              <Button variant="outlined" size="small" onClick={handleDownloadErrorFile}>
                Download error file
              </Button>
            </Box>
          )}

          {successSummary && (
            <Box sx={styles.summary}>
              <Alert severity="success" sx={styles.feedback}>
                {`"${successSummary.fileName ?? uploadedFileName ?? 'File'}" was processed successfully.`}
              </Alert>
              <Box sx={styles.summaryStats}>
                <Typography variant="body2">Batch ID: {successSummary.batchId}</Typography>
                <Typography variant="body2">Total rows: {successSummary.totalRows}</Typography>
                <Typography variant="body2">Success: {successSummary.successCount}</Typography>
                <Typography variant="body2">Failed: {successSummary.failedCount}</Typography>
                <Typography variant="body2">
                  Processing time: {formatProcessingTime(successSummary.processingTimeMs)}
                </Typography>
              </Box>

              {successSummary.addedPatients.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={styles.summaryTableLabel}>
                    Added patients ({successSummary.addedPatients.length})
                  </Typography>
                  <Box sx={styles.summaryTableWrapper}>
                    <DataGrid
                      sx={{height: summaryTableHeight(successSummary.addedPatients.length)}}
                      density="compact"
                      hideFooter
                      rows={successSummary.addedPatients}
                      columns={addedPatientColumns}
                    />
                  </Box>
                </>
              )}

              {successSummary.addedDevices.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={styles.summaryTableLabel}>
                    Added devices ({successSummary.addedDevices.length})
                  </Typography>
                  <Box sx={styles.summaryTableWrapper}>
                    <DataGrid
                      sx={{height: summaryTableHeight(successSummary.addedDevices.length)}}
                      density="compact"
                      hideFooter
                      rows={successSummary.addedDevices}
                      columns={addedDeviceColumns}
                    />
                  </Box>
                </>
              )}

              {successSummary.updatedPatients.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={styles.summaryTableLabel}>
                    Updated patients ({successSummary.updatedPatients.length})
                  </Typography>
                  <Box sx={styles.summaryTableWrapper}>
                    <DataGrid
                      sx={{height: summaryTableHeight(successSummary.updatedPatients.length)}}
                      density="compact"
                      hideFooter
                      rows={successSummary.updatedPatients}
                      columns={addedPatientColumns}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}

          {uploading && (
            <Box sx={styles.progress} role="status" aria-live="polite">
              <CircularProgress size={20} />
              <Typography variant="body2" sx={styles.progressLabel}>
                {loadingStep ?? 'Uploading file...'}
              </Typography>
            </Box>
          )}

          {step === 'select' && (
            <>
              <FileDropZone
                fileTypeLabel="CSV"
                accept={['.csv', 'text/csv', 'application/vnd.ms-excel']}
                disabled={uploading}
                helperText="or click to choose a file from your computer"
                onFilesSelected={handleFilesSelected}
              />

              {fileDetails}
            </>
          )}

          {step === 'preview' && (
            <Box sx={styles.summary}>
              <Typography variant="subtitle2" sx={styles.summaryTableLabel}>
                {`Showing ${previewRows.length} of ${previewTotalRowCount} rows`}
              </Typography>
              <Box sx={styles.summaryTableWrapper}>
                <DataGrid
                  sx={{height: summaryTableHeight(previewRows.length)}}
                  density="compact"
                  hideFooter
                  rows={previewRows}
                  columns={previewColumns}
                />
              </Box>
              {previewTotalRowCount === 0 && (
                <Typography variant="body2" color="text.secondary">
                  This file has no data rows.
                </Typography>
              )}
            </Box>
          )}

          {step === 'validate' && (
            <Box sx={styles.summary}>
              {(validating || approving) && (
                <Box sx={styles.progress} role="status" aria-live="polite">
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={styles.progressLabel}>
                    {approving ? 'Uploading file...' : 'Validating file...'}
                  </Typography>
                </Box>
              )}

              <AlertMessage error={validationError} severity="error" sx={styles.feedback} />

              {validationResult && !approving && (
                <>
                  <ValidationSummaryTiles result={validationResult} />

                  {validationIssueRows.length > 0 ? (
                    <Box sx={styles.summaryTableWrapper}>
                      <DataGrid
                        sx={{height: summaryTableHeight(validationIssueRows.length)}}
                        density="compact"
                        hideFooter
                        rows={validationIssueRows}
                        columns={validationRowColumns}
                        // No getRowHeight override — every row stays the same compact-density
                        // height regardless of how many issues it carries. A row with several
                        // messages scrolls internally (validationRowColumns' Reason cell)
                        // instead of growing the row.
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No errors or warnings found.
                    </Typography>
                  )}

                  {warningNote}
                </>
              )}
            </Box>
          )}

          {step === 'review' && (
            <Box sx={styles.summary}>
              {approving && (
                <Box sx={styles.progress} role="status" aria-live="polite">
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={styles.progressLabel}>
                    Uploading file...
                  </Typography>
                </Box>
              )}

              {validationResult && !approving && (
                <>
                  <Typography variant="subtitle2" sx={styles.summaryTableLabel}>
                    Ready to import
                  </Typography>

                  <Alert severity="info" sx={styles.feedback}>
                    {`Importing "${selectedFile?.name ?? 'this file'}" will save all `}
                    {validationResult.totalRows} row(s) to the database.
                  </Alert>

                  <ValidationSummaryTiles result={validationResult} />

                  {warningNote}

                  <Typography variant="body2" color="text.secondary" sx={{marginTop: 1}}>
                    Nothing is saved until you confirm. This import is all-or-nothing: if any
                    row fails, the entire import is rolled back and no records are created.
                  </Typography>
                </>
              )}
            </Box>
          )}

          <Box sx={styles.buttonContainer}>
            <Button sx={styles.cancelButton} color="warning" onClick={handleCancel}>
              Cancel
            </Button>
            {step === 'preview' && (
              <>
                <Button variant="outlined" onClick={handleBackToSelect} disabled={uploading}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleValidate}
                  disabled={
                    !selectedFile || uploading || validating || previewTotalRowCount === 0
                  }>
                  Validate
                </Button>
              </>
            )}
            {step === 'validate' && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleClearSelection}
                  disabled={uploading || validating}>
                  Fix &amp; Re-upload
                </Button>
                {validationError && !validationResult && (
                  <Button variant="outlined" onClick={handleValidate} disabled={validating}>
                    Retry validation
                  </Button>
                )}
                {/* Opens the review step — it does not upload. Nothing is written until the
                    user confirms there. The zero-errors gate below is permanent, not
                    provisional: POST /csv/upload is strictly all-or-nothing (one DB
                    transaction; any row failure aborts and rolls back the whole batch), there
                    is no partial-commit endpoint, and per the 2026-07-20 product decision none
                    will be added. A file with errors must be fixed and re-uploaded — a subset
                    is never persisted. Approving a zero-error result persists every row,
                    warnings included. */}
                <Button
                  variant="contained"
                  onClick={handleReview}
                  disabled={
                    !validationResult ||
                    validationResult.errorCount > 0 ||
                    uploading ||
                    validating ||
                    approving
                  }>
                  Review &amp; Approve
                </Button>
              </>
            )}
            {step === 'review' && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleBackToValidate}
                  disabled={uploading || approving}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmImport}
                  disabled={
                    !validationResult ||
                    validationResult.errorCount > 0 ||
                    uploading ||
                    approving
                  }>
                  Confirm &amp; Import
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  )
}

export default ImportCsvPage
